<script setup lang="ts">
import { useEmissionBudgets } from "@/api/composables/useEmissionBudgets.composable";
import { useProjectList } from "@/api/composables/useProjectList.composable";
import { useProjectListUpdatedSubscription, Order_By } from "@/graphql";
import { useSpeckleStore } from "@/stores/speckle/speckle.store";
import { useAuthStateStore } from "@/stores/Utils/auth/AuthState";
import { storeToRefs } from "pinia";
import { ref, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import ProjectList from "../views/ProjectList.vue";
import ToolbarWrapper from "@/features/ToolbarWrapper/ToolbarWrapper.vue";
import ProjectListFilters from "../views/ProjectListFilters.vue";
import ProjectListSortSelect from "../components/ProjectListSortSelect.vue";
import PageContentWrapper from "@/features/PageContentWrapper/PageContentWrapper.vue";
import CompareTray from "@/components/Views/MaterialMapping/Library/Compare/CompareTray.vue";
import type { CompareItem } from "@/composables/useLibrarySelection";
import { getUsersByTenantId } from "@/api/services/Frontegg";
import User from "@/types/auth/User";
import * as Sentry from "@sentry/vue";
import { useToast } from "vue-toastification";
import SortOptions, { ProjectListSortOption } from "../constants/SortOptions";
import { ProjectFilterInput } from "@/api/types/Project";
import getProjectQueryWhere from "@/api/utils/getProjectQueryWhere";
import getProjectOrderBy from "@/api/utils/getProjectOrderBy";
import { useHasuraTokenSettled } from "@/composables/useHasuraTokenSettled";
import isProjectFromCurrentTenant from "../utils/isProjectFromCurrentTenant";
import DefaultSkeletonCount from "../constants/DefaultSkeletonCount";
import { useProjectSelection, MAX_PROJECT_COMPARE, type ProjectCompareItem } from "../composables/useProjectSelection";
import { markProjectsPageReady, resetProjectsPageReady } from "../composables/useProjectsPageReadySignal";

const SORT_STORAGE_KEY = "gaiup-projectList-sortOption";
const VIEW_STORAGE_KEY = "gaiup-projectList-view";

function loadStoredSortOption(): ProjectListSortOption {
  try {
    const stored = localStorage.getItem(SORT_STORAGE_KEY);
    if (stored) {
      const parsed: { field: string; direction: string } = JSON.parse(stored);
      const baseOption = SortOptions.value.find((option) => option.orderBy.field === parsed.field);
      if (baseOption && (parsed.direction === Order_By.asc_nulls_last || parsed.direction === Order_By.desc_nulls_last)) {
        return {
          ...baseOption,
          orderBy: {
            field: baseOption.orderBy.field,
            direction: parsed.direction as Order_By,
          },
        };
      }
    }
  } catch {
    // corrupted data — use default
  }
  return SortOptions.value[0];
}

function loadStoredView(): "grid" | "list" {
  try {
    const stored = localStorage.getItem(VIEW_STORAGE_KEY);
    if (stored === "list" || stored === "grid") return stored;
  } catch {
    // use default
  }
  return "grid";
}

const { fetchProjectList, projects, projectsLoading, upsertProject } = useProjectList();
const { clearEmissionBudgets, clearEmissionBudgetsByProjectIds, emissionBudgetsByProjectId, fetchEmissionBudgets } =
  useEmissionBudgets();
const authState = useAuthStateStore();
const { hasAccessAllProjects, user } = storeToRefs(authState);
const hasLoadedUser = computed(() => !!user.value);
const projectSelection = useProjectSelection();
const toast = useToast();
const router = useRouter();
const listView = ref<"grid" | "list">(loadStoredView());
const projectListRef = ref<InstanceType<typeof ProjectList>>();
const projectsInViewPort = ref<Record<string, boolean>>({});
const searchString = ref<string>("");
const selectedFilters = ref<ProjectFilterInput>();
const selectedSortOption = ref<ProjectListSortOption>(loadStoredSortOption());
const skeletonCount = ref<number>(DefaultSkeletonCount);
const tenantUsersById = ref<{ [key: string]: User }>({});

onMounted(async () => {
  document.title = "Projects - Real-Time LCA";

  try {
    const speckle = useSpeckleStore();
    await speckle.refreshToken();
  } catch (error) {
    Sentry.captureException(error);
    console.error("Failed to fetch Speckle token");
  }
});

const { isTokenSettledForTenant } = useHasuraTokenSettled();
const subscriptionVariables = computed(() =>
  getProjectQueryWhere(selectedFilters.value, searchString.value, user.value?.tenantId)
);
// isTokenSettledForTenant keeps the subscription paused between a tenant switch and the new
// tenant's Hasura JWT landing — otherwise it (re)subscribes as the previous tenant (RTLCA-5588).
const subscriptionEnabled = computed(() => hasAccessAllProjects.value !== undefined && isTokenSettledForTenant.value);

const { onResult: onProjectListUpdated } = useProjectListUpdatedSubscription(subscriptionVariables, () => ({
  enabled: subscriptionEnabled.value,
}));

const hasActiveFilters = computed(
  () =>
    !!(
      (searchString.value?.length || 0) > 0 ||
      selectedFilters.value?.buildingTypeLabels.length ||
      selectedFilters.value?.calculationTypeLabels.length ||
      selectedFilters.value?.countries.length ||
      selectedFilters.value?.projectStatuses.length !== undefined ||
      searchString.value?.trim().length
    )
);

const loading = computed(() => projectsLoading.value || !hasLoadedUser.value);

// Signals the welcome tour's last new-user step can safely anchor to the empty-state button, which
// only exists once the project list fetch has settled.
watch(
  loading,
  (isLoading) => {
    if (!isLoading) markProjectsPageReady();
  },
  { immediate: true }
);

onUnmounted(() => {
  resetProjectsPageReady();
});

const handleFetchProjects = async ({
  filters,
  immediately,
  orderBy,
  scrollToTop = true,
  search,
}: {
  filters: ProjectFilterInput | undefined;
  immediately?: boolean;
  orderBy: ProjectListSortOption | undefined;
  scrollToTop?: boolean;
  search: string;
}) => {
  if (hasAccessAllProjects.value === undefined) {
    return;
  }

  if (scrollToTop) {
    projectListRef.value?.scrollToTop();
  }

  return fetchProjectList({
    hasAccessAllProjects: hasAccessAllProjects.value,
    immediately: immediately,
    filters,
    orderBy: orderBy ? getProjectOrderBy(orderBy.orderBy) : undefined,
    searchString: search,
  });
};

const handleFilterUpdate = async (newFilters: ProjectFilterInput) => {
  skeletonCount.value = DefaultSkeletonCount;
  selectedFilters.value = newFilters;

  return handleFetchProjects({
    filters: newFilters,
    orderBy: selectedSortOption.value,
    search: searchString.value,
    immediately: true,
  });
};

const handleRefetchEmissionBudget = (projectId: string) => {
  return fetchEmissionBudgets([projectId], true);
};

const handleReloadClick = () => {
  skeletonCount.value = projects.value.length || DefaultSkeletonCount;
  projectsInViewPort.value = {};
  clearEmissionBudgets();

  return handleFetchProjects({
    filters: selectedFilters.value,
    immediately: true,

    orderBy: selectedSortOption.value,
    scrollToTop: false,
    search: searchString.value,
  });
};

const handleSortOptionChange = async (orderBy: ProjectListSortOption["orderBy"]) => {
  skeletonCount.value = projects.value.length || DefaultSkeletonCount;
  const option = { ...SortOptions.value.find((x) => x.orderBy.field === orderBy.field), orderBy };

  if (option?.label) {
    const input = {
      ...option,
      label: option.label,
    };

    selectedSortOption.value = input;

    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify(orderBy));
    } catch {
      // storage write failed — non-critical
    }

    return handleFetchProjects({
      filters: selectedFilters.value,
      immediately: true,
      orderBy: { ...option, label: option.label },
      search: searchString.value,
    });
  }
};

const handleTableHeaderSort = (field: ProjectListSortOption["orderBy"]["field"]) => {
  const isSameField = selectedSortOption.value.orderBy.field === field;
  const currentDirection = selectedSortOption.value.orderBy.direction;
  const newDirection = isSameField
    ? currentDirection === Order_By.asc_nulls_last
      ? Order_By.desc_nulls_last
      : Order_By.asc_nulls_last
    : SortOptions.value.find((x) => x.orderBy.field === field)?.orderBy.direction ?? Order_By.asc_nulls_last;

  return handleSortOptionChange({ field, direction: newDirection });
};

const handleViewChange = (view: typeof listView.value) => {
  listView.value = view;
  try {
    localStorage.setItem(VIEW_STORAGE_KEY, view);
  } catch {
    // storage write failed — non-critical
  }
};

// The per-card compare checkbox is the single way to build a comparison (no separate select mode): it
// toggles the item in/out of the capped, session-persistent compare set (mirrors MaterialsDatabase.vue).
const handleCompareItem = (item: ProjectCompareItem) => {
  const added = projectSelection.toggleItem(item);
  if (!added) toast.info(`You can compare up to ${MAX_PROJECT_COMPARE} at a time`);
};

const handleCompare = () => {
  if (projectSelection.selectedCount.value < 2) return;
  router.push({ name: "projectCompare", query: { ids: projectSelection.selectedIds.value.join(",") } });
};

// CompareTray shares useLibrarySelection's CompareItem shape ({ id, name, isConstruction }) so it can be
// reused as-is here; projects have no construction/material distinction, so isConstruction is always false.
const compareTrayItems = computed<CompareItem[]>(() =>
  projectSelection.selectedItems.value.map((item) => ({ ...item, isConstruction: false }))
);

onProjectListUpdated(async ({ data }) => {
  if (!data?.project.length) return;
  const project = data.project[0];

  if (project) {
    if (!isProjectFromCurrentTenant(project, user.value?.tenantId)) return;

    const updatedAt = projects.value.find((x) => x.id === project.id)?.updatedAt;

    if (!updatedAt || (project.updatedAt && new Date(project.updatedAt) > new Date(updatedAt))) {
      clearEmissionBudgetsByProjectIds([project.id]);
      upsertProject(project);
      fetchEmissionBudgets([project.id]);
    }
  }
});

const onProjectsInViewPort = (projectIds: string[]) => {
  fetchEmissionBudgets(projectIds);
  projectsInViewPort.value = {
    ...projectsInViewPort.value,
    ...Object.fromEntries(projectIds.map((id) => [id, true])),
  };
};

watch(searchString, (value) => {
  skeletonCount.value = DefaultSkeletonCount;
  handleFetchProjects({ filters: selectedFilters.value, orderBy: selectedSortOption.value, search: value });
});

watch(
  user,
  async (newUser) => {
    if (newUser && newUser.tenantId) {
      try {
        const response = await getUsersByTenantId(newUser.tenantId);
        const users: User[] = response && Array.isArray(response.items) ? response.items : [];

        tenantUsersById.value = users.reduce<Record<string, User>>((acc, x) => {
          acc[x.id] = x;
          return acc;
        }, {});
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  },
  { immediate: true }
);
</script>

<template>
  <ToolbarWrapper>
    <ProjectListFilters
      v-model:search-string="searchString"
      :loading="loading"
      :has-access-all-projects="hasAccessAllProjects"
      :view="listView"
      @reload="handleReloadClick"
      @update="handleFilterUpdate"
      @view-change="handleViewChange">
      <ProjectListSortSelect
        :options="SortOptions"
        :value="selectedSortOption"
        title="Sort projects"
        @change="handleSortOptionChange" />
    </ProjectListFilters>
  </ToolbarWrapper>
  <PageContentWrapper>
    <ProjectList
      ref="projectListRef"
      :emission-budgets-by-project-id="emissionBudgetsByProjectId"
      :loading="loading"
      :has-active-filters="hasActiveFilters"
      :projects="projects"
      :selected-for-bulk="projectSelection.selectedIds.value"
      :skeleton-count="skeletonCount"
      :sort-direction="selectedSortOption.orderBy.direction"
      :sort-field="selectedSortOption.orderBy.field"
      :tenant-users-by-id="tenantUsersById"
      :view="listView"
      @projects-in-view-port="onProjectsInViewPort"
      @refetch-emission-budget="handleRefetchEmissionBudget"
      @sort="handleTableHeaderSort"
      @compare-item="handleCompareItem" />

    <!-- Persistent compare-later tray (mirrors Library's CompareTray/useLibrarySelection pattern) -->
    <Transition name="fade-bottom">
      <CompareTray
        v-if="projectSelection.selectedCount.value > 0"
        class="z-sticky sticky bottom-0"
        :items="compareTrayItems"
        :max-items="MAX_PROJECT_COMPARE"
        @remove="projectSelection.removeItem"
        @clear="projectSelection.clearSelection"
        @compare="handleCompare" />
    </Transition>
  </PageContentWrapper>
</template>
